import { ComponentProps } from "react";
import styled from "styled-components";
import { Input as BaseInput } from "../../components/input/Input";
import { primary01, shade0, shade600, shade900 } from "../../styles/tokens";
import { normalStyles } from "../body/Body.Parts";

// TEXT

// STYLES
export const SearchButton = styled.button<{ width?: string }>`
  top: 0;
  width: ${({ width }) => width || "3rem"};
  height: 3rem;
  display: flex;
  z-index: 1;
  cursor: pointer;
  position: absolute;
  align-items: center;
  pointer-events: all;
  justify-content: center;

  transition:
    translate 0.3s ease-in-out,
    background-color 0.1s,
    color 0.1s;

  color: ${shade0};

  &:hover {
    color: ${primary01};
    background-color: ${shade900};
  }

  &:focus-visible {
    outline: 1px solid ${shade0};
    outline-offset: -2px;
    color: ${primary01};
  }
`;

export const Input = styled(BaseInput)`
  height: 100%;
  border: none;
  padding: 0 1.5rem 0 3rem;
  background-color: transparent;

  transition:
    background-color 0.2s,
    color 0.2s;

  &:focus-within {
    border: none;
  }

  input {
    max-height: unset;

    &:focus {
      max-height: unset;
    }

    /* The real placeholder is in the PlaceholderOverlay, so hide the native one */
    &::placeholder {
      color: transparent;
    }
  }
`;

export const PlaceholderOverlay = styled.span`
  position: absolute;
  left: 3rem;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
  color: ${shade600};
  ${normalStyles};
`;

export const InputWrapper = styled.div`
  width: 100%;
  translate: 0;
  display: flex;
  position: relative;
  align-items: center;

  transition: translate 0.3s ease-in-out;

  &:has(input:not(:placeholder-shown)) ${PlaceholderOverlay} {
    display: none;
  }
`;

export const ClearButtonWrapper = styled.div`
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  width: 1.5rem;
  padding: 0 0.5rem;
  position: absolute;
  align-items: center;
  box-sizing: content-box;
`;

export const ClearButton = styled.button`
  scale: 1;
  width: 100%;
  display: flex;
  height: 1.5rem;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  box-sizing: content-box;

  transition:
    scale 0.3s ease-in-out,
    color 0.2s;

  color: ${shade0};

  &:hover {
    color: ${primary01};
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 1px ${shade0};
  }
`;

export const Wrapper = styled.div<{
  $active?: boolean;
  $type?: "left" | "right" | "fullWidth";
}>`
  height: 3rem;
  display: flex;
  width: ${({ $type }) => ($type === "fullWidth" ? "100%" : "19.5rem")};
  overflow: hidden;
  position: relative;
  ${({ $type }) => $type !== "fullWidth" && "container-type: inline-size;"}
  ${InputWrapper} {
    background: ${shade900};

    ${({ $active, $type }) => {
      if ($active || $type === "fullWidth") return;

      const x = $type === "left" ? "-100%" : "100%";
      return `translate: ${x} 0;`;
    }}
  }

  ${SearchButton} {
    ${({ $type }) => {
      if ($type === "fullWidth")
        return `
      left: 0;
      right: unset;
      pointer-events: none;
      color: ${shade0};
      `;
      return $type === "left" ? `left: 0;` : `right: 0;`;
    }}

    ${({ $active, $type }) => {
      if (!$active || $type === "left" || $type === "fullWidth") return;

      return `translate: calc(-100cqw + 100%) 0;`;
    }}
  }

  ${Input}:has(input:placeholder-shown) ~ ${ClearButtonWrapper} ${ClearButton} {
    scale: 0;
    visibility: hidden;
  }

  // Focus within the entire search component
  // Commented out because there are no designs for this state
  /* &:has(${Input} input:focus-visible) {
    outline: 1px solid ${shade0};
  } */
`;

// DATA

// TYPES
export interface Props extends Omit<ComponentProps<"input">, "type"> {
  label?: string;
  hasError?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  defaultActive?: boolean;
  width?: string;
  type?: "left" | "right" | "fullWidth";
}

// CONSTANTS

// FUNCTIONS

// ASSETS
